import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { usePermissions, ResourceType, ActionType } from '../lib/hooks/usePermissions';
import { Loader2 } from 'lucide-react';
import { PermissionDeniedPage } from './PermissionDeniedPage';
import { PermissionErrorPage } from './PermissionErrorPage';

interface ProtectedRouteProps {
  children: React.ReactNode;
  resource?: ResourceType;
  action?: ActionType;
  requiresPermission?: boolean;
}

export function ProtectedRoute({
  children,
  resource,
  action,
  requiresPermission = false
}: ProtectedRouteProps) {
  const location = useLocation();
  const { user, loading: authLoading, twoFactorPending } = useAuth();
  const {
    hasPermission,
    hasResourceAccess,
    loading: permissionsLoading,
    error: permissionsError,
    permissions
  } = usePermissions();

  // CRITICAL FIX: Check if we're in initial loading state (loading AND no permissions yet)
  // This prevents false denials during the race condition window
  const isInitialLoading = authLoading || (permissionsLoading && permissions.length === 0);

  // Show loader during initial authentication or initial permissions load
  if (isInitialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-black animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Block access if 2FA verification is pending (prevents SPA navigation bypass)
  // BUT allow access to /2fa-setup page for users who need to complete setup
  if (twoFactorPending) {
    if (location.pathname === '/2fa-setup') {
      return <>{children}</>;
    }
    return <Navigate to="/login" replace />;
  }

  // If this route doesn't require specific permissions, render the children
  if (!requiresPermission || (!resource && !action)) {
    return <>{children}</>;
  }

  if (permissionsError) {
    return <PermissionErrorPage error={permissionsError} />;
  }

  // Check permissions based on provided resource and action
  let hasAccess = true;

  if (resource && action) {
    hasAccess = hasPermission(resource, action);
  } else if (resource && !action) {
    hasAccess = hasResourceAccess(resource);
  }

  if (!hasAccess) {
    // Estrai la risorsa dal path per una migliore UX
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const resourceFromPath = pathSegments[0] || resource || 'questa sezione';
    const actionToShow = action || 'visualizzare';

    return (
      <PermissionDeniedPage
        resource={resourceFromPath}
        action={actionToShow}
        requiredPermission={resource && action ? `${resource}_${action}` : undefined}
      />
    );
  }

  // User is authenticated and has the required permissions
  return <>{children}</>;
}

/**
 * Route guard per le pagine Super Admin.
 * Richiede is_super_admin=true dal DB (non da localStorage).
 */
export function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, rawIsSuperAdmin: isSuperAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-black animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isSuperAdmin) {
    return <PermissionDeniedPage resource="superadmin" action="manage" />;
  }

  return <>{children}</>;
}

/**
 * Route guard per le pagine Partner Admin.
 * Richiede is_partner_admin=true dal DB (non da localStorage).
 */
export function PartnerAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, rawIsPartnerAdmin: isPartnerAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-black animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isPartnerAdmin) {
    return <PermissionDeniedPage resource="partner_admin" action="manage" />;
  }

  return <>{children}</>;
}